import { cs } from '../../lib/currency';
import { useEffect, useRef, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  AlertCircle,
  Bell,
  Trash2,
  CreditCard,
  Loader2,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import EditSeriesDialog from '../../components/admin/EditSeriesDialog';
import CreditBalanceBanner from '../../components/customer/CreditBalanceBanner';
import EquipmentPicker from '../../components/admin/EquipmentPicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar as CalendarPicker } from '../../components/ui/calendar';

// Extracted dialog + helper modules — keep this page focused on the
// availability grid + cart logic.
import { DURATION_OPTIONS } from '../../components/customer/lane_booking/constants';
import {
  formatDate, formatTime, computeEndTime, getPrice as _getPrice,
} from '../../components/customer/lane_booking/utils';
import WaitlistModal from '../../components/customer/lane_booking/WaitlistModal';
import RescheduleModal from '../../components/customer/lane_booking/RescheduleModal';
import {
  CancelBookingAlertDialog,
  LeaveWaitlistAlertDialog,
  DragRescheduleAlertDialog,
} from '../../components/customer/lane_booking/AlertDialogs';
import AvailabilityGrid from '../../components/customer/lane_booking/AvailabilityGrid';
import MyBookingsTab from '../../components/customer/lane_booking/MyBookingsTab';
import BookingCart from '../../components/customer/lane_booking/BookingCart';
import PaymentMethodChooser from '../../components/checkout/PaymentMethodChooser';
import {
  useBusinessTimezone,
  getTodayInTz,
  nowInTz,
  addDaysToDateString,
  compareDateStrings,
  parseDateOnly,
  formatDateOnlyDisplay,
} from '../../utils/businessTime';

const LaneBooking = () => {
  // The whole booking flow is anchored on the academy's business timezone.
  // A parent in California should still see "today" as the same day the
  // academy sees — otherwise they'd be unable to book the current slot.
  const businessTz = useBusinessTimezone();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [editSeriesOpen, setEditSeriesOpen] = useState(false);
  const [editSeriesBooking, setEditSeriesBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lanes, setLanes] = useState([]);
  const [availability, setAvailability] = useState(null);
  // selectedDate uses the business-TZ "today" as its initial value so the
  // grid never opens on the wrong day for off-tz visitors.
  const [selectedDate, setSelectedDate] = useState(() => getTodayInTz('America/New_York'));
  const [hasNavigatedDate, setHasNavigatedDate] = useState(false);
  useEffect(() => {
    if (!businessTz || hasNavigatedDate) return;
    const businessToday = getTodayInTz(businessTz);
    setSelectedDate((prev) => (prev === businessToday ? prev : businessToday));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTz]);
  const [myBookings, setMyBookings] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [cancelBookingDialogOpen, setCancelBookingDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [leaveWaitlistDialogOpen, setLeaveWaitlistDialogOpen] = useState(false);
  const [waitlistToLeave, setWaitlistToLeave] = useState(null);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [dragRescheduleData, setDragRescheduleData] = useState(null);
  
  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    duration: '30',
    notes: '',
    recurrence: 'none',
    equipment_rentals: [],
    terms_accepted: false,
    // Card vs ACH — set by PaymentMethodChooser before proceed-to-pay.
    payment_method_choice: null,
  });
  const [equipmentTotal, setEquipmentTotal] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(false);

  // ----- Multi-lane cart -----
  // Customers can stack several slot selections into a cart and pay
  // for them all in one Stripe Checkout. Each cart item snapshots the
  // slot/lane/duration/price at add-time so prices stay stable while
  // they shop. Stored in component state only — no localStorage so a
  // refresh resets the cart (slots may have been taken anyway).
  const [cartBookings, setCartBookings] = useState([]);
  const [bulkCheckoutLoading, setBulkCheckoutLoading] = useState(false);
  
  // Payment state
  const [paymentStep, setPaymentStep] = useState('details'); // 'details' | 'payment' | 'processing' | 'success'
  const [paymentData, setPaymentData] = useState(null); // Holds client_secret, amount, etc.

  // iter319 — saved-card status for "Quick Book" one-click pay. Fetched
  // once on mount; the webhook updates `auto_pay_method_on_file` after
  // the customer's FIRST successful checkout, so this is stable for the
  // session.
  const [savedPmStatus, setSavedPmStatus] = useState({ can_quick_book: false });
  const [quickBookLoading, setQuickBookLoading] = useState(false);
  
  // Waitlist state
  const [myWaitlist, setMyWaitlist] = useState([]);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({
    lane_id: '',
    preferred_start_time: '',
    duration: '30',
    notes: ''
  });

  // Reschedule state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedBookingToReschedule, setSelectedBookingToReschedule] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    lane_id: '',
    date: '',
    start_time: '',
    duration: '30'
  });

  // Drag and drop state
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [dropTargetSlot, setDropTargetSlot] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Guards against stale availability responses arriving out-of-order
  // when the user rapidly navigates between dates.
  const availabilityRequestId = useRef(0);

  useEffect(() => {
    fetchLanes();
    fetchMyBookings();
    fetchMyWaitlist();
    fetchSavedPmStatus();
  }, []);

  useEffect(() => {
    fetchAvailability();
  }, [selectedDate]);

  const fetchLanes = async () => {
    try {
      const response = await api.get('/lanes');
      setLanes(response.data);
    } catch (error) {
      console.error('Error fetching lanes:', error);
    }
  };

  const fetchAvailability = async () => {
    const requestId = ++availabilityRequestId.current;
    const requestedDate = selectedDate;
    setLoading(true);
    try {
      const response = await api.get(`/lanes/availability?date=${requestedDate}`);
      // Drop this response if a newer fetch has been kicked off meanwhile,
      // or if the user has moved to a different date — prevents stale data
      // from overwriting fresh data on rapid clicks.
      if (requestId !== availabilityRequestId.current) return;
      setAvailability(response.data);
    } catch (error) {
      if (requestId !== availabilityRequestId.current) return;
      console.error('Error fetching availability:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      }
    } finally {
      if (requestId === availabilityRequestId.current) {
        setLoading(false);
      }
    }
  };

  const fetchMyBookings = async () => {
    try {
      const response = await api.get('/lanes/bookings');
      setMyBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchMyWaitlist = async () => {
    try {
      const response = await api.get('/lanes/waitlist');
      setMyWaitlist(response.data);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
    }
  };

  // iter319 — fetch saved-PM eligibility so the booking modal can offer
  // a "Quick Book" button to returning customers.
  const fetchSavedPmStatus = async () => {
    try {
      const response = await api.get('/lanes/bookings/saved-pm-status');
      setSavedPmStatus(response.data || { can_quick_book: false });
    } catch (error) {
      // Silent — quick-book is an enhancement, not a blocker.
      setSavedPmStatus({ can_quick_book: false });
    }
  };

  const handleJoinWaitlist = async () => {
    try {
      await api.post('/lanes/waitlist', {
        ...waitlistForm,
        date: selectedDate
      });
      toast.success('Added to waitlist! You will be notified when a slot opens up.');
      setShowWaitlistModal(false);
      setWaitlistForm({ lane_id: '', preferred_start_time: '', duration: '30', notes: '' });
      fetchMyWaitlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    }
  };

  const handleLeaveWaitlist = async (waitlistId) => {
    setWaitlistToLeave(waitlistId);
    setLeaveWaitlistDialogOpen(true);
  };

  const confirmLeaveWaitlist = async () => {
    if (!waitlistToLeave) return;
    try {
      await api.delete(`/lanes/waitlist/${waitlistToLeave}`);
      toast.success('Removed from waitlist');
      setLeaveWaitlistDialogOpen(false);
      setWaitlistToLeave(null);
      fetchMyWaitlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave waitlist');
    }
  };

  const openWaitlistModal = (laneId, time) => {
    setWaitlistForm({
      lane_id: laneId,
      preferred_start_time: time,
      duration: '30',
      notes: ''
    });
    setShowWaitlistModal(true);
  };

  const navigateDate = (days) => {
    // Pure string math — never crosses DST boundaries weirdly because we
    // use UTC noon underneath. Then validate against business-TZ "today".
    const next = addDaysToDateString(selectedDate, days);
    const todayStr = getTodayInTz(businessTz);
    const maxStr = addDaysToDateString(todayStr, 30);
    if (compareDateStrings(next, todayStr) >= 0 && compareDateStrings(next, maxStr) <= 0) {
      setHasNavigatedDate(true);
      setSelectedDate(next);
    }
  };

  const handleCalendarSelect = (date) => {
    if (!date) return;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const pickedStr = `${year}-${month}-${day}`;
    const todayStr = getTodayInTz(businessTz);
    const maxStr = addDaysToDateString(todayStr, 30);
    if (compareDateStrings(pickedStr, todayStr) >= 0 && compareDateStrings(pickedStr, maxStr) <= 0) {
      setHasNavigatedDate(true);
      setSelectedDate(pickedStr);
      setCalendarOpen(false);
    } else {
      toast.error('Please select a date within the next 30 days');
    }
  };

  // Parse selectedDate as local date for calendar
  const getSelectedDateObject = () => parseDateOnly(selectedDate);

  // Get min/max dates for calendar — based on business-TZ today.
  const getCalendarDateRange = () => {
    const todayStr = getTodayInTz(businessTz);
    return {
      from: parseDateOnly(todayStr),
      to: parseDateOnly(addDaysToDateString(todayStr, 30)),
    };
  };

  // formatDate / formatTime / computeEndTime are imported from lane_booking/utils.

  const openBookingModal = (lane, slot) => {
    setSelectedSlot({ lane, slot });
    setBookingForm({ duration: '30', notes: '', recurrence: 'none', equipment_rentals: [], terms_accepted: false, payment_method_choice: null });
    setPaymentStep('details');
    setPaymentData(null);
    setShowBookingModal(true);
  };

  // Thin wrapper so the (laneId, duration) call sites in this file stay
  // unchanged; the shared util needs the lanes array.
  const getPrice = (laneId, duration) => _getPrice(lanes, laneId, duration);

  // Check for returning from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const payment = urlParams.get('payment');
    const cancelledBookingId = urlParams.get('booking_id');
    
    if (payment === 'success' && sessionId) {
      // Poll for payment confirmation
      pollPaymentStatus(sessionId);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'cancelled' && cancelledBookingId) {
      toast.error('Payment was cancelled. The booking has been removed.');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh data
      fetchAvailability();
      fetchMyBookings();
    }
  }, []);

  // Poll for payment status after returning from Stripe
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your bookings.');
      return;
    }
    
    try {
      const response = await api.get(`/lanes/bookings/checkout-status/${sessionId}`);
      
      if (response.data.success && response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your booking is confirmed.');
        fetchAvailability();
        fetchMyBookings();
        return;
      } else if (response.data.payment_status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status.');
    }
  };

  // iter319 — Quick Book using saved card on file. Bypasses Stripe
  // Checkout redirect entirely; backend charges the saved PM
  // off-session and confirms the booking in one shot. On hard failures
  // (SCA challenge, card declined) we surface the error and the user
  // can still fall back to the standard "Proceed to Payment" flow.
  const handleQuickBook = async () => {
    if (!selectedSlot) return;
    if (!bookingForm.terms_accepted) {
      toast.error('Please accept the Terms & Conditions to continue.');
      return;
    }
    setQuickBookLoading(true);
    try {
      const response = await api.post('/lanes/bookings/quick-book', {
        lane_id: selectedSlot.lane.lane_id,
        date: selectedDate,
        start_time: selectedSlot.slot.start_time,
        duration: bookingForm.duration,
        recurrence: bookingForm.recurrence,
        notes: bookingForm.notes || null,
        equipment_rentals: bookingForm.equipment_rentals || [],
        terms_accepted: !!bookingForm.terms_accepted,
        payment_method_choice: 'card',
      });
      if (response.data.requires_payment === false) {
        // Credit-only path — server told us to redirect via confirm-payment.
        await handleConfirmWithCredits();
        return;
      }
      toast.success(
        response.data.card_brand && response.data.card_last4
          ? `Booked! Charged ${response.data.card_brand} •••• ${response.data.card_last4}.`
          : 'Lane booked successfully!',
      );
      setShowBookingModal(false);
      fetchAvailability();
      fetchMyBookings();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // 402 SCA / decline — guide user to fall back to standard Checkout.
      if (error.response?.status === 402 && detail && typeof detail === 'object') {
        toast.error(
          detail.code === 'sca_required'
            ? 'Your bank requires additional authentication. Please use "Proceed to Payment" instead.'
            : detail.message || 'Card declined. Please use "Proceed to Payment" instead.',
          { duration: 6000 },
        );
      } else if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'past_due_invoices') {
        toast.error(
          `${detail.message} Open My Bookings → Pay Now to settle them.`,
          { duration: 8000 },
        );
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Quick book failed');
      }
    } finally {
      setQuickBookLoading(false);
    }
  };

  // Step 1: Proceed to payment - create Stripe Checkout session
  const handleProceedToPayment = async () => {
    if (!selectedSlot) return;
    
    setBookingLoading(true);
    try {
      console.log('Creating payment intent...');
      const response = await api.post('/lanes/bookings/create-payment-intent', {
        lane_id: selectedSlot.lane.lane_id,
        date: selectedDate,
        start_time: selectedSlot.slot.start_time,
        duration: bookingForm.duration,
        recurrence: bookingForm.recurrence,
        notes: bookingForm.notes || null,
        equipment_rentals: bookingForm.equipment_rentals || [],
        terms_accepted: !!bookingForm.terms_accepted,
        payment_method_choice: bookingForm.payment_method_choice,
        origin_url: window.location.origin
      });
      
      console.log('Payment intent response:', response.data);
      setPaymentData(response.data);
      
      if (!response.data.requires_payment) {
        // Can use credits only - confirm immediately
        await handleConfirmWithCredits();
      } else if (response.data.checkout_url) {
        const checkoutUrl = response.data.checkout_url;
        const sessionId = response.data.session_id;
        console.log('Stripe Checkout URL:', checkoutUrl);
        
        // Close the modal and show full-screen redirect state
        setShowBookingModal(false);
        setPaymentStep('redirecting');
        setBookingLoading(false);
        
        // Open Stripe in a NEW TAB to avoid iframe restrictions
        setTimeout(() => {
          console.log('Opening Stripe checkout in new tab:', checkoutUrl);
          const stripeWindow = window.open(checkoutUrl, '_blank');
          
          // If popup was blocked, show manual link
          if (!stripeWindow || stripeWindow.closed || typeof stripeWindow.closed === 'undefined') {
            console.log('Popup blocked - user needs to click manual link');
            toast.info('Please click the "Open Payment Page" button to continue');
          }
          
          // Start polling for payment status
          if (sessionId) {
            console.log('Starting payment status polling for session:', sessionId);
            startPaymentPolling(sessionId);
          }
        }, 500);
        return;
      } else {
        console.error('No checkout_url in response:', response.data);
        toast.error('Failed to initialize payment - no checkout URL received');
        setPaymentStep('details');
      }
    } catch (error) {
      console.error('Payment init error:', error);
      console.error('Error response:', error.response?.data);
      const detail = error.response?.data?.detail;
      // 409 + structured past_due_invoices payload → friendlier message
      // listing how much they owe so they can clear it from the parent
      // billing page before retrying.
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'past_due_invoices') {
        toast.error(
          `${detail.message} Open My Bookings → Pay Now to settle them.`,
          { duration: 8000 },
        );
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to initialize payment');
      }
    } finally {
      setBookingLoading(false);
    }
  };

  // ----- Cart helpers -----
  // Add the currently-configured slot to the cart and close the booking
  // modal so the customer can keep browsing for more slots.
  const handleAddToCart = () => {
    if (!selectedSlot) return;
    if (!bookingForm.terms_accepted) {
      toast.error('Please accept the Terms & Conditions to continue.');
      return;
    }
    const dup = cartBookings.find(
      (c) =>
        c.lane_id === selectedSlot.lane.lane_id &&
        c.date === selectedDate &&
        c.start_time === selectedSlot.slot.start_time,
    );
    if (dup) {
      toast.error('This slot is already in your cart.');
      return;
    }
    // Lane object uses `lane_name` (not `name`) and slot rows do not
    // carry a price — derive both from already-loaded state.
    const laneNameDisplay = selectedSlot.lane.lane_name || `Lane ${selectedSlot.lane.lane_id}`;
    const lanePrice = Number(getPrice(selectedSlot.lane.lane_id, bookingForm.duration)) || 0;
    const cartItem = {
      lane_id: selectedSlot.lane.lane_id,
      lane_name: laneNameDisplay,
      date: selectedDate,
      start_time: selectedSlot.slot.start_time,
      duration: bookingForm.duration,
      notes: bookingForm.notes || null,
      equipment_rentals: bookingForm.equipment_rentals || [],
      terms_accepted: true,
      // Display-only — final price gets re-resolved server-side.
      display_price: lanePrice + Number(equipmentTotal || 0),
    };
    setCartBookings((prev) => [...prev, cartItem]);
    setShowBookingModal(false);
    toast.success(`${laneNameDisplay} added to cart`);
  };

  const _removeFromCart = (idx) => {
    setCartBookings(cartBookings.filter((_, i) => i !== idx));
  };

  // Pay for all cart items in one Stripe Checkout via the bulk endpoint.
  // Two-step UX: clicking "Pay" on the floating cart opens a confirm
  // dialog with the Card-vs-ACH chooser; only after picking does the
  // request fire.
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkPmChoice, setBulkPmChoice] = useState(null);
  const openBulkCheckout = () => {
    setBulkPmChoice(null);
    setShowBulkConfirm(true);
  };

  const handleBulkCheckout = async () => {
    if (cartBookings.length === 0) return;
    if (!bulkPmChoice) return;
    setBulkCheckoutLoading(true);
    try {
      const response = await api.post('/lanes/bookings/create-bulk-payment-intent', {
        bookings: cartBookings.map(({ display_price, lane_name, ...b }) => b),
        terms_accepted: true,
        payment_method_choice: bulkPmChoice,
        origin_url: window.location.origin,
      });
      if (response.data.checkout_url) {
        // Stripe path — open Checkout in a new tab.
        const win = window.open(response.data.checkout_url, '_blank');
        if (!win || win.closed) {
          toast.info('Popup blocked — please allow popups or click the link manually.');
        }
        setCartBookings([]);
      } else if (response.data.requires_payment === false) {
        // Credit-only path — bookings already confirmed.
        toast.success(response.data.message || 'Bookings confirmed using credits!');
        setCartBookings([]);
        // Refresh the page so the new bookings show in "My Bookings".
        if (typeof fetchMyBookings === 'function') fetchMyBookings();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'past_due_invoices') {
        toast.error(
          `${detail.message} Open My Bookings → Pay Now to settle them.`,
          { duration: 8000 },
        );
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create combined checkout');
      }
    } finally {
      setBulkCheckoutLoading(false);
    }
  };

  const cartTotal = cartBookings.reduce((s, c) => s + (Number(c.display_price) || 0), 0);
  // Poll for payment status when waiting for payment in new tab
  const startPaymentPolling = (sessionId, attempts = 0) => {
    const maxAttempts = 60; // Poll for up to 5 minutes (60 * 5 seconds)
    const pollInterval = 5000; // 5 seconds
    
    if (attempts >= maxAttempts) {
      console.log('Payment polling timed out');
      toast.info('Payment check timed out. Click "Check Payment Status" to verify.');
      return;
    }
    
    setTimeout(async () => {
      try {
        console.log(`Polling payment status (attempt ${attempts + 1})...`);
        const response = await api.get(`/lanes/bookings/checkout-status/${sessionId}`);
        
        if (response.data.payment_status === 'paid' || response.data.status === 'complete') {
          console.log('Payment confirmed!');
          toast.success('Payment successful! Your lane is booked.');
          setPaymentStep('success');
          fetchAvailability();
          fetchMyBookings();
        } else if (response.data.payment_status === 'cancelled' || response.data.payment_status === 'expired') {
          console.log('Payment cancelled or expired');
          toast.error('Payment was cancelled or expired.');
          setPaymentStep('details');
          setShowBookingModal(true);
        } else {
          // Continue polling
          startPaymentPolling(sessionId, attempts + 1);
        }
      } catch (error) {
        console.log('Payment status check failed, continuing to poll...', error);
        // Continue polling even on error
        startPaymentPolling(sessionId, attempts + 1);
      }
    }, pollInterval);
  };

  // Confirm booking with credits only (no Stripe payment needed)
  const handleConfirmWithCredits = async () => {
    setPaymentStep('processing');
    try {
      const _response = await api.post('/lanes/bookings/confirm-payment', {
        lane_id: selectedSlot.lane.lane_id,
        date: selectedDate,
        start_time: selectedSlot.slot.start_time,
        duration: bookingForm.duration,
        recurrence: bookingForm.recurrence,
        notes: bookingForm.notes || null,
        equipment_rentals: bookingForm.equipment_rentals || [],
        terms_accepted: !!bookingForm.terms_accepted,
      }, {
        params: {
          use_credits_only: true
        }
      });
      
      setPaymentStep('success');
      toast.success('Booking confirmed! Paid with account credit.');
      setTimeout(() => {
        setShowBookingModal(false);
        fetchAvailability();
        fetchMyBookings();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
      setPaymentStep('details');
    }
  };

  // Legacy booking function (kept for compatibility but not used in new flow)
  const _handleBooking = async () => {
    if (!selectedSlot) return;
    
    setBookingLoading(true);
    try {
      const _response = await api.post('/lanes/bookings', {
        lane_id: selectedSlot.lane.lane_id,
        date: selectedDate,
        start_time: selectedSlot.slot.start_time,
        duration: bookingForm.duration,
        recurrence: bookingForm.recurrence,
        notes: bookingForm.notes || null,
        equipment_rentals: bookingForm.equipment_rentals || [],
        terms_accepted: !!bookingForm.terms_accepted,
      });
      
      toast.success('Booking confirmed!');
      setShowBookingModal(false);
      fetchAvailability();
      fetchMyBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    setBookingToCancel(bookingId);
    setCancelBookingDialogOpen(true);
  };

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;
    try {
      const response = await api.post(`/lanes/bookings/${bookingToCancel}/cancel`, {});
      toast.success(`Booking cancelled. ${response.data.refund_type === 'full_refund' ? 'Full refund applied.' : response.data.refund_type === 'credit' ? 'Credit added to your account.' : 'No refund (cancelled within 6 hours).'}`);
      setCancelBookingDialogOpen(false);
      setBookingToCancel(null);
      fetchMyBookings();
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cancellation failed');
    }
  };

  // Reschedule functions
  const openRescheduleModal = (booking) => {
    setSelectedBookingToReschedule(booking);
    setRescheduleForm({
      lane_id: booking.lane_id,
      date: booking.date,
      start_time: booking.start_time,
      duration: String(booking.duration || 30)
    });
    setShowRescheduleModal(true);
  };

  const handleReschedule = async () => {
    if (!selectedBookingToReschedule) return;
    
    try {
      await api.put(
        `/lanes/bookings/${selectedBookingToReschedule.id}/reschedule`,
        rescheduleForm
      );
      toast.success('Booking rescheduled successfully');
      setShowRescheduleModal(false);
      setSelectedBookingToReschedule(null);
      fetchMyBookings();
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule booking');
    }
  };

  // Check if booking can be rescheduled (at least 2 hours before)
  const canReschedule = (booking) => {
    // booking.date + booking.start_time are stored in business TZ already;
    // compare against business-TZ "now" so the 2-hour window is correct
    // regardless of the visitor's browser timezone.
    if (!booking?.date || !booking?.start_time) return false;
    const bookingStr = `${booking.date}T${booking.start_time.length === 5 ? booking.start_time + ':00' : booking.start_time}`;
    const nowStr = nowInTz(businessTz);
    // Both strings are local-tz "YYYY-MM-DDTHH:MM:SS" with the same TZ
    // implicit, so JS Date arithmetic via local-time parsing gives a
    // consistent delta (the parser applies the SAME local offset to both).
    const bookingMs = new Date(bookingStr).getTime();
    const nowMs = new Date(nowStr).getTime();
    const hoursUntil = (bookingMs - nowMs) / (1000 * 60 * 60);
    return hoursUntil >= 2;
  };

  // Drag and drop handlers
  const handleDragStart = (e, booking) => {
    if (!canReschedule(booking)) {
      e.preventDefault();
      toast.error('Cannot reschedule bookings less than 2 hours away');
      return;
    }
    setDraggedBooking(booking);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', booking.id);
    e.dataTransfer.setData('application/json', JSON.stringify(booking));
    // Add a slight delay for visual feedback
    setTimeout(() => {
      if (e.target) e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    if (e.target) e.target.style.opacity = '1';
    setIsDragging(false);
    setDraggedBooking(null);
    setDropTargetSlot(null);
  };

  const handleDragOver = (e, laneId, startTime, isAvailable) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAvailable && draggedBooking) {
      e.dataTransfer.dropEffect = 'move';
      setDropTargetSlot({ laneId, startTime });
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only clear if we're leaving the drop zone entirely
    const relatedTarget = e.relatedTarget;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDropTargetSlot(null);
    }
  };

  const handleDrop = async (e, laneId, startTime) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetSlot(null);
    
    if (!draggedBooking) return;
    
    // Show confirmation dialog
    setDragRescheduleData({
      booking: draggedBooking,
      laneId,
      startTime
    });
    setRescheduleDialogOpen(true);
  };

  const confirmDragReschedule = async () => {
    if (!dragRescheduleData) return;
    const { booking, laneId, startTime } = dragRescheduleData;
    
    try {
      await api.put(`/lanes/bookings/${booking.id}/reschedule`, {
        lane_id: laneId,
        date: selectedDate,
        start_time: startTime,
        duration: String(booking.duration || 30)
      });
      
      toast.success('Booking rescheduled successfully!');
      setRescheduleDialogOpen(false);
      setDragRescheduleData(null);
      fetchMyBookings();
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule booking');
    } finally {
      setDraggedBooking(null);
      setIsDragging(false);
    }
  };

  const cancelDragReschedule = () => {
    setRescheduleDialogOpen(false);
    setDragRescheduleData(null);
    setDraggedBooking(null);
    setIsDragging(false);
  };

  // Check if a slot has user's booking (for showing draggable booking)
  const getUserBookingForSlot = (laneId, startTime) => {
    return myBookings.find(
      b => b.lane_id === laneId && 
           b.date === selectedDate && 
           b.start_time === startTime && 
           b.status === 'confirmed'
    );
  };

  // Check if a slot is a continuation of a multi-slot booking/block (not the start)
  const getCustomerSlotSpanInfo = (laneSlot, lane, idx) => {
    if (!laneSlot) return { skip: false, rowSpan: 1 };

    // User's own booking: check if this is the start slot
    const userBooking = getUserBookingForSlot(lane.lane_id, laneSlot.start_time);
    if (userBooking && userBooking.duration > 30) {
      const bookingStartTime = userBooking.start_time;
      if (laneSlot.start_time === bookingStartTime) {
        return { skip: false, rowSpan: Math.ceil(userBooking.duration / 30), type: 'own' };
      }
      // Continuation of own booking
      return { skip: true, rowSpan: 0, type: 'own' };
    }

    // Other person's booking: is_booking_start marks the first slot
    if (laneSlot.booking_id && laneSlot.is_booking_start && (laneSlot.booking_duration || 0) > 30) {
      return { skip: false, rowSpan: Math.ceil(laneSlot.booking_duration / 30), type: 'other' };
    }
    if (laneSlot.booking_id && !laneSlot.is_booking_start && !userBooking) {
      return { skip: true, rowSpan: 0, type: 'other' };
    }

    // Block/coaching: check block_reason and look at consecutive blocked slots
    if (!laneSlot.is_available && !laneSlot.booking_id && laneSlot.block_reason) {
      // Check if the previous slot had the same block — if so, this is a continuation
      if (idx > 0) {
        const prevSlot = lane.slots[idx - 1];
        if (prevSlot && !prevSlot.is_available && !prevSlot.booking_id && prevSlot.block_reason === laneSlot.block_reason) {
          return { skip: true, rowSpan: 0, type: 'block' };
        }
      }
      // This is the start of a block — count consecutive blocked slots with same reason
      let span = 1;
      for (let i = idx + 1; i < lane.slots.length; i++) {
        const nextSlot = lane.slots[i];
        if (nextSlot && !nextSlot.is_available && !nextSlot.booking_id && nextSlot.block_reason === laneSlot.block_reason) {
          span++;
        } else break;
      }
      return { skip: false, rowSpan: span, type: 'block' };
    }

    return { skip: false, rowSpan: 1 };
  };

  // "Today" in the business timezone — used for splitting upcoming vs past
  // bookings. The sort uses string comparison (date+time concatenated) so
  // we don't fall back to `new Date()` browser-local parsing for either.
  const today = getTodayInTz(businessTz);
  const bookingKey = (b) => `${b.date} ${b.start_time}`;

  const upcomingBookings = myBookings.filter(b =>
    b.status === 'confirmed' && b.date >= today
  ).sort((a, b) => (bookingKey(a) < bookingKey(b) ? -1 : bookingKey(a) > bookingKey(b) ? 1 : 0));

  const pastBookings = myBookings.filter(b =>
    b.date < today || b.status === 'cancelled'
  ).sort((a, b) => (bookingKey(a) < bookingKey(b) ? 1 : bookingKey(a) > bookingKey(b) ? -1 : 0));
  
  const pendingPaymentBookings = myBookings.filter(b => 
    b.payment_status !== 'paid' && b.status === 'confirmed'
  );

  if (loading && !availability) {
    return (
      <DashboardLayout title="Lane Booking">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Full-screen redirect overlay
  if (paymentStep === 'redirecting') {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900 flex items-center justify-center" data-testid="redirect-overlay">
        <div className="text-center p-8 max-w-lg">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-blue-600 flex items-center justify-center animate-pulse">
              <CreditCard className="h-10 w-10 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">
            Complete Payment in New Tab
          </h2>
          <p className="text-slate-300 mb-4">
            A new tab has opened with the Stripe payment page. Please complete your payment there.
          </p>
          <div className="flex items-center justify-center gap-2 text-green-400 mb-6">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Waiting for payment confirmation...</span>
          </div>
          
          {/* Manual redirect button - PROMINENT */}
          {paymentData?.checkout_url && (
            <div className="bg-slate-800 rounded-lg p-6 mb-6">
              <p className="text-amber-400 font-semibold mb-4">
                ⚠️ If the payment tab didn't open, click below:
              </p>
              <a 
                href={paymentData.checkout_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors"
                onClick={() => console.log('Manual redirect clicked')}
              >
                Open Payment Page (New Tab) →
              </a>
              <p className="text-slate-400 text-sm mt-3">
                Stripe checkout will open in a new browser tab
              </p>
            </div>
          )}
          
          {/* Status message */}
          <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
            <p className="text-slate-300 text-sm">
              This page will automatically update once your payment is complete.
            </p>
            <p className="text-slate-400 text-xs mt-2">
              Session: {paymentData?.session_id?.substring(0, 20)}...
            </p>
          </div>
          
          {/* Check payment status button */}
          <button
            onClick={() => {
              if (paymentData?.session_id) {
                pollPaymentStatus(paymentData.session_id);
              }
            }}
            className="text-blue-400 hover:text-blue-300 text-sm underline mb-4 block mx-auto"
          >
            Check Payment Status
          </button>
          
          {/* Cancel button */}
          <button
            onClick={() => {
              setPaymentStep('details');
              setShowBookingModal(true);
            }}
            className="text-slate-400 hover:text-white text-sm underline"
          >
            Cancel and go back
          </button>
        </div>
      </div>
    );
  }

  // Payment success screen
  if (paymentStep === 'success') {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900 flex items-center justify-center" data-testid="payment-success-overlay">
        <div className="text-center p-8 max-w-lg">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-600 flex items-center justify-center">
              <Check className="h-10 w-10 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">
            Payment Successful!
          </h2>
          <p className="text-slate-300 mb-6">
            Your lane has been booked successfully. You can close the payment tab if it's still open.
          </p>
          
          <div className="bg-slate-800 rounded-lg p-4 mb-6 text-left">
            <p className="text-slate-400 text-sm mb-2">Booking Details:</p>
            <p className="text-white">Amount Paid: {cs()}{paymentData?.total_price || paymentData?.amount_to_charge || '0'}</p>
          </div>
          
          <button
            onClick={() => {
              setPaymentStep('details');
              setPaymentData(null);
              fetchAvailability();
              fetchMyBookings();
            }}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition-colors"
          >
            Done - View My Bookings
          </button>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout title="Lane Booking">
      <div className="space-y-6" data-testid="lane-booking-page">
        {/* Pricing Info */}
        <Card className="border border-slate-200 bg-gradient-to-r from-blue-50 to-green-50">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Lane 1 & 2</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>30 min: $30</p>
                  <p>1 hour: $55</p>
                  <p>1.5 hours: $80</p>
                  <p>2 hours: $110</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Lane 3 & 4</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>30 min: $25</p>
                  <p>1 hour: $50</p>
                  <p>1.5 hours: $75</p>
                  <p>2 hours: $100</p>
                </div>
              </div>
              <div className="col-span-2">
                <h4 className="font-semibold text-slate-700 mb-2">Cancellation Policy</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <p className="flex items-center gap-1"><Check className="h-3 w-3 text-green-500" /> Free cancellation 24+ hours before</p>
                  <p className="flex items-center gap-1"><AlertCircle className="h-3 w-3 text-yellow-500" /> Account credit 6-24 hours before</p>
                  <p className="flex items-center gap-1"><X className="h-3 w-3 text-red-500" /> No refund within 6 hours</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date Navigation */}
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateDate(-1)}
                disabled={selectedDate <= getTodayInTz(businessTz)}
                data-testid="prev-date-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-center">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 hover:bg-slate-100" data-testid="date-picker-btn">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      <div className="text-left">
                        <h2 className="font-heading text-xl font-semibold text-slate-900">
                          {formatDate(selectedDate)}
                        </h2>
                        <p className="text-sm text-slate-500">
                          {availability?.is_open ? 
                            `Open: ${formatTime(availability.business_hours?.open_time)} - ${formatTime(availability.business_hours?.close_time)}` :
                            'Closed'
                          }
                        </p>
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <CalendarPicker
                      mode="single"
                      selected={getSelectedDateObject()}
                      onSelect={handleCalendarSelect}
                      disabled={(date) => {
                        const range = getCalendarDateRange();
                        return date < range.from || date > range.to;
                      }}
                      initialFocus
                    />
                    <div className="p-2 border-t text-center">
                      <p className="text-xs text-slate-500">Book up to 30 days ahead</p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateDate(1)}
                data-testid="next-date-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Availability Grid */}
        <AvailabilityGrid
          availability={availability}
          selectedDate={selectedDate}
          loading={loading}
          isDragging={isDragging}
          dropTargetSlot={dropTargetSlot}
          canReschedule={canReschedule}
          getUserBookingForSlot={getUserBookingForSlot}
          getCustomerSlotSpanInfo={getCustomerSlotSpanInfo}
          onOpenBookingModal={openBookingModal}
          onOpenWaitlistModal={openWaitlistModal}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        />

        {/* Upcoming Bookings */}
        <MyBookingsTab
          myBookings={myBookings}
          lanes={lanes}
          isAdmin={isAdmin}
          upcomingBookings={upcomingBookings}
          pendingPaymentBookings={pendingPaymentBookings}
          pastBookings={pastBookings}
          canReschedule={canReschedule}
          onCancelBooking={handleCancelBooking}
          onOpenReschedule={openRescheduleModal}
          onOpenEditSeries={(b) => { setEditSeriesBooking(b); setEditSeriesOpen(true); }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onRefresh={fetchMyBookings}
        />
      </div>

      {/* Floating cart bar — extracted to BookingCart.jsx */}
      <BookingCart
        cartBookings={cartBookings}
        cartTotal={cartTotal}
        bulkCheckoutLoading={bulkCheckoutLoading}
        onClear={() => setCartBookings([])}
        onCheckout={openBulkCheckout}
      />

      {/* Bulk-checkout confirm — Card vs ACH chooser before redirect. */}
      <Dialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <DialogContent className="max-w-md" data-testid="bulk-checkout-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Pay {cartBookings.length} booking{cartBookings.length === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>
              Choose how you'd like to pay. ACH (bank transfer) skips the
              {' '}3% processing fee.
            </DialogDescription>
          </DialogHeader>
          <PaymentMethodChooser
            baseAmount={Number(cartTotal) || 0}
            value={bulkPmChoice}
            onChange={setBulkPmChoice}
            disabled={bulkCheckoutLoading}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkConfirm(false)} disabled={bulkCheckoutLoading}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setShowBulkConfirm(false);
                await handleBulkCheckout();
              }}
              disabled={!bulkPmChoice || bulkCheckoutLoading}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="bulk-checkout-confirm-btn"
            >
              {bulkCheckoutLoading
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Redirecting…</>
                : 'Continue to Checkout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={(open) => {
        if (!open) {
          setPaymentStep('details');
          setPaymentData(null);
        }
        setShowBookingModal(open);
      }}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {paymentStep === 'success' ? (
                <Check className="h-5 w-5 text-green-500" />
              ) : paymentStep === 'payment' ? (
                <CreditCard className="h-5 w-5 text-blue-500" />
              ) : (
                <Calendar className="h-5 w-5 text-blue-500" />
              )}
              {paymentStep === 'success' ? 'Booking Confirmed!' : 
               paymentStep === 'payment' ? 'Complete Payment' :
               `Book ${selectedSlot?.lane?.lane_name}`}
            </DialogTitle>
          </DialogHeader>
          
          {/* Step 1: Booking Details */}
          {paymentStep === 'details' && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Date:</strong> {formatDate(selectedDate)}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Start Time:</strong> {selectedSlot?.slot?.start_time && formatTime(selectedSlot.slot.start_time)}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Duration</Label>
                <Select
                  value={bookingForm.duration}
                  onValueChange={(value) => setBookingForm({ ...bookingForm, duration: value })}
                >
                  <SelectTrigger data-testid="duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label} - {cs()}{getPrice(selectedSlot?.lane?.lane_id, opt.value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Recurring (Optional)</Label>
                <Select
                  value={bookingForm.recurrence}
                  onValueChange={(value) => setBookingForm({ ...bookingForm, recurrence: value })}
                >
                  <SelectTrigger data-testid="recurrence-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">One-time booking</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                  placeholder="Any special requests..."
                  rows={2}
                  data-testid="notes-input"
                />
              </div>

              <EquipmentPicker
                date={selectedDate}
                startTime={selectedSlot?.slot?.start_time}
                endTime={computeEndTime(selectedSlot?.slot?.start_time, bookingForm.duration)}
                value={bookingForm.equipment_rentals || []}
                onChange={(lines) => setBookingForm({ ...bookingForm, equipment_rentals: lines })}
                onTotalChange={setEquipmentTotal}
              />

              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center justify-between text-sm text-blue-800">
                  <span>Lane:</span>
                  <span>{cs()}{Number(getPrice(selectedSlot?.lane?.lane_id, bookingForm.duration) || 0).toFixed(2)}</span>
                </div>
                {equipmentTotal > 0 && (
                  <div className="flex items-center justify-between text-sm text-blue-800 mt-0.5">
                    <span>Equipment:</span>
                    <span>+ {cs()}{Number(equipmentTotal).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-1 pt-1 border-t border-blue-200">
                  <span className="font-medium text-blue-900">Total Price:</span>
                  <span className="text-xl font-bold text-blue-900">
                    {cs()}{(Number(getPrice(selectedSlot?.lane?.lane_id, bookingForm.duration) || 0) + Number(equipmentTotal || 0)).toFixed(2)}
                  </span>
                </div>
              </div>

              <CreditBalanceBanner
                amount={(parseFloat(getPrice(selectedSlot?.lane?.lane_id, bookingForm.duration)) || 0) + Number(equipmentTotal || 0)}
              />

              {/* Terms & Conditions consent — required for any reservation. */}
              <div
                className={`flex items-start gap-2 p-3 rounded-md border ${
                  bookingForm.terms_accepted ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'
                }`}
                data-testid="customer-booking-terms-block"
              >
                <Checkbox
                  id="customer-booking-terms"
                  checked={!!bookingForm.terms_accepted}
                  onCheckedChange={(checked) => setBookingForm({ ...bookingForm, terms_accepted: !!checked })}
                  data-testid="customer-booking-terms-checkbox"
                  className="mt-0.5"
                />
                <Label htmlFor="customer-booking-terms" className="text-sm cursor-pointer leading-snug">
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline"
                    data-testid="customer-booking-terms-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms &amp; Conditions
                  </a>
                  <span className="text-rose-600"> *</span>
                </Label>
              </div>

              {/* Card vs ACH chooser — must be picked before redirect.
                  Replaces the old "Stripe shows both, surcharge added,
                  ACH auto-refund later" pattern. */}
              <PaymentMethodChooser
                baseAmount={(parseFloat(getPrice(selectedSlot?.lane?.lane_id, bookingForm.duration)) || 0) + Number(equipmentTotal || 0)}
                value={bookingForm.payment_method_choice || null}
                onChange={(v) => setBookingForm({ ...bookingForm, payment_method_choice: v })}
                disabled={bookingLoading}
              />

              <DialogFooter className="pt-2 flex-wrap gap-2">
                <Button variant="outline" onClick={() => setShowBookingModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={handleAddToCart}
                  disabled={bookingLoading || !bookingForm.terms_accepted}
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  data-testid="add-to-cart-btn"
                  title="Add to cart and continue browsing for more slots"
                >
                  + Add to cart
                </Button>
                {/* iter319 — Quick Book using saved card on file. Only
                    appears when the tenant allows it AND the user has a
                    PM on file from a prior booking. Skips Stripe Checkout
                    entirely (off-session PaymentIntent confirm). */}
                {savedPmStatus.can_quick_book && (
                  <Button
                    onClick={handleQuickBook}
                    disabled={quickBookLoading || bookingLoading || !bookingForm.terms_accepted}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                    data-testid="quick-book-btn"
                    title={
                      savedPmStatus.brand && savedPmStatus.last4
                        ? `Charge ${savedPmStatus.brand} •••• ${savedPmStatus.last4} now`
                        : 'Charge saved card on file'
                    }
                  >
                    {quickBookLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Booking…
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        {savedPmStatus.brand && savedPmStatus.last4
                          ? `Pay with •••• ${savedPmStatus.last4}`
                          : 'Quick Book'}
                      </>
                    )}
                  </Button>
                )}
                <Button 
                  onClick={handleProceedToPayment} 
                  disabled={bookingLoading || !bookingForm.terms_accepted || !bookingForm.payment_method_choice}
                  className="bg-blue-500 hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="proceed-to-payment-btn"
                >
                  {bookingLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Proceed to Payment
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Redirecting to Stripe */}
          {paymentStep === 'redirecting' && (
            <div className="py-12 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-900">Redirecting to payment...</p>
              <p className="text-sm text-slate-500">You'll be redirected to our secure payment page.</p>
            </div>
          )}

          {/* Step 3: Processing */}
          {paymentStep === 'processing' && (
            <div className="py-12 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-900">Confirming your booking...</p>
              <p className="text-sm text-slate-500">Please wait while we process your payment.</p>
            </div>
          )}

          {/* Step 4: Success */}
          {paymentStep === 'success' && (
            <div className="py-12 text-center">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-lg font-medium text-slate-900 mb-2">Booking Confirmed!</p>
              <p className="text-sm text-slate-500">
                Your lane has been booked for {formatDate(selectedDate)} at {selectedSlot?.slot?.start_time && formatTime(selectedSlot.slot.start_time)}.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* My Waitlist */}
      {myWaitlist.length > 0 && (
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-500" />
              My Waitlist
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-slate-100">
            {myWaitlist.map(entry => (
              <div key={entry.id} className="p-4 flex items-center justify-between" data-testid={`waitlist-entry-${entry.id}`}>
                <div>
                  <p className="font-medium text-slate-900">{entry.lane_name}</p>
                  <p className="text-sm text-slate-500">
                    {formatDateOnlyDisplay(entry.date, { weekday: 'short', month: 'short', day: 'numeric', year: undefined })} at {formatTime(entry.preferred_start_time)}
                  </p>
                  <Badge className={entry.status === 'notified' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {entry.status === 'notified' ? 'Slot Available!' : 'Waiting'}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleLeaveWaitlist(entry.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`leave-waitlist-${entry.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <WaitlistModal
        open={showWaitlistModal}
        onOpenChange={setShowWaitlistModal}
        selectedDate={selectedDate}
        waitlistForm={waitlistForm}
        setWaitlistForm={setWaitlistForm}
        onConfirm={handleJoinWaitlist}
      />

      <RescheduleModal
        open={showRescheduleModal}
        onOpenChange={setShowRescheduleModal}
        selectedBooking={selectedBookingToReschedule}
        rescheduleForm={rescheduleForm}
        setRescheduleForm={setRescheduleForm}
        lanes={lanes}
        onConfirm={handleReschedule}
      />

      <CancelBookingAlertDialog
        open={cancelBookingDialogOpen}
        onOpenChange={setCancelBookingDialogOpen}
        onConfirm={confirmCancelBooking}
      />

      <LeaveWaitlistAlertDialog
        open={leaveWaitlistDialogOpen}
        onOpenChange={setLeaveWaitlistDialogOpen}
        onConfirm={confirmLeaveWaitlist}
      />

      <DragRescheduleAlertDialog
        open={rescheduleDialogOpen}
        onOpenChange={(open) => { if (!open) cancelDragReschedule(); }}
        dragData={dragRescheduleData}
        onConfirm={confirmDragReschedule}
        onCancel={cancelDragReschedule}
      />

      <EditSeriesDialog
        open={editSeriesOpen}
        onOpenChange={setEditSeriesOpen}
        pivotBooking={editSeriesBooking}
        lanes={lanes}
        onSaved={() => {
          fetchMyBookings();
          fetchAvailability();
        }}
      />
    </DashboardLayout>
  );
};

export default LaneBooking;
